import { useMemo } from 'react';

import { ComputedNodeStatus, NodeStatePhase, NodeStatus } from '@cast/types';

import { formatPercentage } from '@cast/utils';
import { MetricsBox } from 'components/boxes';

import { SummaryItem } from './SummaryItem';

import { NODE_STATE_PHASE_LABELS } from '../../../../constants/labels';
import { PreparedNode } from '../../../types';
import { aggregateNodeCountByStatus } from '../../../utils';
import { useCluster } from '@cast/shared-cluster-context-data-access';

import { Box, colorOrToken, tokens } from '@cast/design-system';

const STATUS_TEST_IDS: Record<NodeStatePhase, string> = {
  [NodeStatus.ready]: 'nodes-summary-ready-count',
  'not-ready': 'nodes-summary-not-ready-count',
  [NodeStatus.creating]: 'nodes-summary-creating-count',
  [NodeStatus.pending]: 'nodes-summary-pending-count',
  [NodeStatus.deleting]: 'nodes-summary-deleting-count',
  [NodeStatus.deleted]: 'nodes-summary-deleted-count',
  [NodeStatus.draining]: 'nodes-summary-draining-count',
  [ComputedNodeStatus.CORDONED]: 'nodes-summary-cordoned-count',
  [ComputedNodeStatus.DISRUPTED]: 'nodes-summary-disrupted-count',
  [NodeStatus.interrupted]: 'nodes-summary-interrupted-count',
  [NodeStatus.unknown]: 'nodes-summary-unknown-count',
  [NodeStatus.detached]: 'nodes-summary-detached-count',
};

type Props = {
  nodes: PreparedNode[];
};

const DISPLAYABLE_METRICS: NodeStatePhase[] = [
  NodeStatus.ready,
  'not-ready',
  NodeStatus.creating,
  NodeStatus.pending,
  NodeStatus.deleting,
  NodeStatus.draining,
  ComputedNodeStatus.CORDONED,
  NodeStatus.interrupted,
  NodeStatus.detached,
  NodeStatus.unknown,
];

const KENT_DISPLAYABLE_METRICS: NodeStatePhase[] = [
  ...DISPLAYABLE_METRICS,
  ComputedNodeStatus.DISRUPTED,
];

const STATUS_COLORS: Record<NodeStatePhase, string> = {
  [NodeStatus.ready]: colorOrToken(
    'green.500',
    tokens.theme.bg.status.successStrong
  ),
  'not-ready': colorOrToken('red.400', tokens.theme.bg.status.errorStrong),
  [NodeStatus.creating]: colorOrToken(
    'grey.400',
    tokens.theme.fg.status.neutral
  ),
  [NodeStatus.pending]: colorOrToken(
    'grey.400',
    tokens.theme.fg.status.neutral
  ),
  [NodeStatus.deleting]: colorOrToken(
    'grey.400',
    tokens.theme.fg.status.neutral
  ),
  [NodeStatus.deleted]: colorOrToken(
    'grey.400',
    tokens.theme.fg.status.neutral
  ),
  [NodeStatus.draining]: colorOrToken(
    'red.400',
    tokens.theme.bg.status.errorStrong
  ),
  [ComputedNodeStatus.CORDONED]: colorOrToken(
    'yellow.600',
    tokens.theme.bg.status.warningStrong
  ),
  [ComputedNodeStatus.DISRUPTED]: colorOrToken(
    'yellow.600',
    tokens.theme.bg.status.warningStrong
  ),
  [NodeStatus.interrupted]: colorOrToken(
    'grey.400',
    tokens.theme.fg.status.neutral
  ),
  [NodeStatus.unknown]: colorOrToken(
    'grey.400',
    tokens.theme.fg.status.neutral
  ),
  [NodeStatus.detached]: colorOrToken(
    'yellow.600',
    tokens.theme.bg.status.warningStrong
  ),
};

type NodeStatusMetricProps = {
  status: NodeStatePhase;
  count: number;
  totalNodes: number;
  shouldMute: boolean;
};

const NodeStatusMetric = ({
  status,
  count,
  totalNodes,
  shouldMute,
}: NodeStatusMetricProps) => {
  const percentage = totalNodes > 0 ? (count / totalNodes) * 100 : 0;
  const mutedFg = tokens.theme.disabled.fg;
  const mutedMarker = tokens.theme.disabled.bg;
  const secondaryFg = colorOrToken(
    'textSecondary',
    tokens.theme.fg.structural.secondary
  );

  return (
    <MetricsBox
      testId={STATUS_TEST_IDS[status]}
      markerColor={shouldMute ? mutedMarker : STATUS_COLORS[status]}
      title={NODE_STATE_PHASE_LABELS[status]}
      titleProps={{
        variant: 'L10R',
        color: shouldMute ? mutedFg : undefined,
      }}
      primaryValue={count}
      primaryValueProps={{
        variant: 'h6',
        color: shouldMute ? mutedFg : undefined,
      }}
      secondaryValue={`(${formatPercentage(percentage, { roundBy: 1 })})`}
      secondaryValueProps={{
        variant: 'P12R',
        color: shouldMute ? mutedFg : secondaryFg,
      }}
      sx={{ width: 'max-content' }}
    />
  );
};

export const NodesStatusesBreakdown = ({ nodes }: Props) => {
  const { isKentManaged, isKentEligible } = useCluster();
  const shouldShowKentMetrics = isKentEligible || isKentManaged;
  const displayableMetrics = shouldShowKentMetrics
    ? KENT_DISPLAYABLE_METRICS
    : DISPLAYABLE_METRICS;
  const nodeCountByStatus = useMemo(
    () => aggregateNodeCountByStatus(nodes),
    [nodes]
  );

  return (
    <SummaryItem testId="nodes-summary-statuses">
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${shouldShowKentMetrics ? 6 : 5}, 80px)`,
          gap: '24px 16px',
        }}
      >
        {displayableMetrics.map((status) => {
          const nodeCount = nodeCountByStatus[status];
          const shouldMute = nodeCount === 0;

          return (
            <NodeStatusMetric
              key={status}
              status={status}
              count={nodeCount}
              totalNodes={nodes.length}
              shouldMute={shouldMute}
            />
          );
        })}
      </Box>
    </SummaryItem>
  );
};
